import type { OverlayPanelLayer } from "@frontend/pages/case-workspace/pathology/components/overlay-panel"
import {
  isOverlayForActiveModule,
  selectModuleBackedOverlays
} from "@frontend/pages/case-workspace/pathology/lib/overlay-module-visibility"
import type { WsiSource } from "@frontend/pages/case-workspace/pathology/types"
import { getLabelAliasResolverForDomain } from "@shared/lib/tissue-seg-gleason"
import type { OverlayColormapName, OverlayLabelHit, PointerMoveEvent, TileViewer } from "@virdx/wsi-viewer"
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"

interface UseWsiOverlaysParams {
  viewerRef: RefObject<TileViewer | null>
  /** Bumps whenever a base image finishes loading into a (re)created viewer. */
  viewerEpoch: number
  wsiSources: WsiSource[] | undefined
  activeSeriesId: string | null
  /** Domains edited by the project's `label_raster` modules; overlays outside this set are never attached. */
  labelRasterDomains: ReadonlySet<string>
  /** Domain of the active `label_raster` module (null for any other module); only its overlay is visible. */
  activeLabelRasterDomain: string | null
}

export interface CursorLabelInfo {
  overlayId: string
  value: number
  name?: string
  color?: [number, number, number]
}

const DECLARED_OVERLAY_OPACITY = 0.6
const MANUAL_OVERLAY_OPACITY = 0.5

/**
 * Mirrors the viewer's overlay layers into React state and owns overlay
 * lifecycle for the pathology workspace:
 * - overlays declared on the case (`role === "overlay"`, `overlayOf === active
 *   slide`) are auto-attached whenever the base image (re)loads, but only when
 *   a `label_raster` module in the project edits their `domain`; an overlay
 *   without a module is never attached and therefore never visible;
 * - exactly the overlay belonging to the active `label_raster` module is
 *   visible; switching modules switches the visible overlay, and with any
 *   other module active every declared overlay is hidden;
 * - "Load as Overlay" adds undeclared sources manually;
 * - visibility / opacity / colormap / per-label opacity handlers;
 * - a throttled readout of the label under the cursor.
 *
 * Domain special cases (e.g. collapsing Gleason sub-labels of the tissue
 * segmentation overlay onto their base classes) are passed to the viewer as a
 * label-alias resolver per overlay; see `@shared/lib/tissue-seg-gleason`.
 */
export function useWsiOverlays({
  viewerRef,
  viewerEpoch,
  wsiSources,
  activeSeriesId,
  labelRasterDomains,
  activeLabelRasterDomain
}: UseWsiOverlaysParams) {
  const [layers, setLayers] = useState<OverlayPanelLayer[]>([])
  const [cursorLabel, setCursorLabel] = useState<CursorLabelInfo | null>(null)
  const manualIdsRef = useRef<Set<string>>(new Set())

  const declaredOverlays = useMemo(
    () => selectModuleBackedOverlays(wsiSources ?? [], activeSeriesId, labelRasterDomains),
    [wsiSources, activeSeriesId, labelRasterDomains]
  )
  const sourcesRef = useRef(wsiSources)
  const declaredIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    sourcesRef.current = wsiSources
    declaredIdsRef.current = new Set(declaredOverlays.map((s) => s.id))
  }, [wsiSources, declaredOverlays])

  const refresh = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer) {
      setLayers([])
      return
    }
    const sources = sourcesRef.current ?? []
    const next: OverlayPanelLayer[] = viewer.getOverlays().map((layer) => {
      const source = sources.find((s) => s.id === layer.sourceName)
      const labelOpacities: Record<number, number> = {}
      for (const label of layer.labels) {
        const opacity = viewer.getOverlayLabelOpacity(layer.id, label.value)
        if (opacity < 1) labelOpacities[label.value] = opacity
      }
      return {
        id: layer.id,
        label: source?.label ?? layer.sourceName,
        domain: source?.domain ?? null,
        visible: layer.visible,
        opacity: layer.opacity,
        colormap: layer.colormap,
        hasMetadataColormap: layer.hasMetadataColormap,
        labels: layer.labels,
        labelOpacities,
        coverage: layer.coverage,
        declared: declaredIdsRef.current.has(layer.id)
      }
    })
    setLayers(next)
  }, [viewerRef])

  // Manual overlays do not survive a viewer (re)creation.
  useEffect(() => {
    manualIdsRef.current.clear()
  }, [viewerEpoch])

  // Auto-attach module-backed declared overlays each time the base image loads.
  // Only the active module's overlay starts visible.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    const existing = new Set(viewer.getOverlays().map((o) => o.id))
    let added = false
    declaredOverlays.forEach((source, index) => {
      if (existing.has(source.id)) return
      added = true
      viewer.addOverlay({
        id: source.id,
        sourceName: source.id,
        sourceType: source.sourceType,
        colormap: "tab10",
        opacity: DECLARED_OVERLAY_OPACITY,
        visible: isOverlayForActiveModule(source, activeLabelRasterDomain),
        zIndex: index,
        labelAliases: getLabelAliasResolverForDomain(source.domain)
      })
    })
    // Only mirror when something changed: an unconditional setState here would
    // re-render for every new `wsiSources` array identity.
    if (added) refresh()
  }, [viewerEpoch, declaredOverlays, activeLabelRasterDomain, refresh, viewerRef])

  // Visibility follows the active module: show the overlay whose domain the
  // active `label_raster` module edits, hide every other declared overlay.
  // Re-runs when the module or the attached set changes, not on every layer
  // refresh, so opacity edits from the module panel are left alone.
  const attachedDeclaredKey = useMemo(
    () =>
      layers
        .filter((l) => l.declared)
        .map((l) => l.id)
        .join("|"),
    [layers]
  )
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    const attached = new Map(viewer.getOverlays().map((o) => [o.id, o] as const))
    let changed = false
    for (const source of declaredOverlays) {
      const layer = attached.get(source.id)
      if (!layer) continue
      const visible = isOverlayForActiveModule(source, activeLabelRasterDomain)
      if (layer.visible === visible) continue
      viewer.setOverlayVisible(source.id, visible)
      changed = true
    }
    if (changed) refresh()
  }, [viewerEpoch, declaredOverlays, activeLabelRasterDomain, attachedDeclaredKey, refresh, viewerRef])

  // Keep React state in sync with viewer overlay events.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        refresh()
      })
    }
    viewer.on("overlay-added", schedule)
    viewer.on("overlay-removed", schedule)
    viewer.on("overlay-change", schedule)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      viewer.off("overlay-added", schedule)
      viewer.off("overlay-removed", schedule)
      viewer.off("overlay-change", schedule)
    }
  }, [viewerEpoch, refresh, viewerRef])

  // Throttled label-under-cursor readout (one in-flight read at a time).
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    let token = 0
    let busy = false
    let pending: { x: number; y: number } | null = null

    const run = async (x: number, y: number) => {
      busy = true
      const myToken = ++token
      let hit: OverlayLabelHit | null = null
      try {
        hit = await viewer.readOverlayLabelValueAt(x, y)
      } catch {
        hit = null
      }
      busy = false
      if (myToken !== token) return
      setCursorLabel((prev) => {
        if (!hit) return prev === null ? prev : null
        if (prev && prev.overlayId === hit.overlayId && prev.value === hit.value) return prev
        return { overlayId: hit.overlayId, value: hit.value, name: hit.name, color: hit.color }
      })
      if (pending) {
        const next = pending
        pending = null
        void run(next.x, next.y)
      }
    }
    const onMove = (e: PointerMoveEvent) => {
      if (!viewer.getOverlays().some((o) => o.visible && o.opacity > 0)) {
        setCursorLabel((prev) => (prev === null ? prev : null))
        return
      }
      if (busy) {
        pending = { x: e.imageX, y: e.imageY }
        return
      }
      void run(e.imageX, e.imageY)
    }
    const onLeave = () => {
      token++
      pending = null
      setCursorLabel(null)
    }
    viewer.on("pointer-move", onMove)
    viewer.on("pointer-leave", onLeave)
    return () => {
      token++
      viewer.off("pointer-move", onMove)
      viewer.off("pointer-leave", onLeave)
    }
  }, [viewerEpoch, viewerRef])

  const setVisible = useCallback(
    (id: string, visible: boolean) => {
      viewerRef.current?.setOverlayVisible(id, visible)
    },
    [viewerRef]
  )
  const setOpacity = useCallback(
    (id: string, opacity: number) => {
      viewerRef.current?.setOverlayOpacity(id, opacity)
    },
    [viewerRef]
  )
  const setColormap = useCallback(
    (id: string, colormap: string) => {
      viewerRef.current?.setOverlayColormap(id, colormap as OverlayColormapName)
    },
    [viewerRef]
  )
  const setLabelOpacity = useCallback(
    (id: string, label: number, opacity: number) => {
      viewerRef.current?.setOverlayLabelOpacity(id, label, opacity)
    },
    [viewerRef]
  )
  const remove = useCallback(
    (id: string) => {
      manualIdsRef.current.delete(id)
      viewerRef.current?.removeOverlay(id)
    },
    [viewerRef]
  )
  const addManual = useCallback(
    (sourceId: string) => {
      const viewer = viewerRef.current
      const source = sourcesRef.current?.find((s) => s.id === sourceId)
      if (!(viewer && source)) return
      if (viewer.getOverlays().some((o) => o.id === sourceId)) return
      manualIdsRef.current.add(sourceId)
      viewer.addOverlay({
        id: sourceId,
        sourceName: sourceId,
        sourceType: source.sourceType,
        colormap: "tab10",
        opacity: MANUAL_OVERLAY_OPACITY,
        visible: true,
        zIndex: 100 + manualIdsRef.current.size,
        labelAliases: getLabelAliasResolverForDomain(source.domain)
      })
    },
    [viewerRef]
  )

  const layerIds = useMemo(() => new Set(layers.map((l) => l.id)), [layers])

  return {
    layers,
    layerIds,
    declaredOverlays,
    cursorLabel,
    setVisible,
    setOpacity,
    setColormap,
    setLabelOpacity,
    remove,
    addManual
  }
}
